// src/api/axiosInstance.js
import axios from "axios";
import {
  resolveIdToken,
  createAuthRetryInterceptor,
  createTransientRetryInterceptor,
} from "./authToken";

// Regional API gateway base URL. Prefer the explicit gateway URL
// (REACT_APP_PDX_API_BASE_URL); fall back to REACT_APP_API_BASE, then to a
// relative path (handled by the dev proxy in setupProxy.js).
const baseURL =
  process.env.REACT_APP_PDX_API_BASE_URL ||
  process.env.REACT_APP_API_BASE ||
  "/";

const api = axios.create({ baseURL: baseURL, timeout: 10_000 });

// Attach a freshly resolved Firebase ID token to every request. resolveIdToken
// lets the SDK serve the cached token until it nears expiry and auto-refreshes
// otherwise, so a clinician's session never needs a manual refresh.
api.interceptors.request.use(async (cfg) => {
  const token = await resolveIdToken();
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

// Recover from a token that the backend rejects mid-session: force one refresh
// and replay the request. A still-rejected retry propagates so the app can
// fall back to sign-in.
api.interceptors.response.use(
  (response) => response,
  createAuthRetryInterceptor((cfg) => api(cfg)),
);

// Retry idempotent requests through transient backend failures (502/503/504),
// e.g. Cloud Run cold starts after an idle tab, so a warm-up blip doesn't
// surface as an error the user has to fix by refreshing. 401s are handled by
// the auth interceptor above; each interceptor re-rejects what it doesn't own.
api.interceptors.response.use(
  (response) => response,
  createTransientRetryInterceptor((cfg) => api(cfg)),
);

export default api;
